import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetCurrentUserQuery, useGetPendingApprovalsForMeQuery } from '@/services/api';
import type { PendingApprovalItem } from '@/types/api';
import { formatAge } from '@/utils/format';
import { Meta } from '@/components/Meta';


type CountdownState = 'ok' | 'warn' | 'breach';

interface Countdown {
  state: CountdownState;
  label: string;
}

function countdownFor(item: PendingApprovalItem, nowMs: number): Countdown {
  if (item.breach_notified) {
    const overMin = item.sla_deadline
      ? Math.max(0, Math.round((nowMs - Date.parse(item.sla_deadline)) / 60000))
      : 0;
    return { state: 'breach', label: `⚠ ${overMin}m over` };
  }
  if (item.sla_deadline === undefined || item.sla_minutes === undefined) {
    return { state: 'ok', label: '—' };
  }
  const remainingMs = Date.parse(item.sla_deadline) - nowMs;
  if (remainingMs <= 0) {
    return { state: 'breach', label: `⚠ ${Math.round(-remainingMs / 60000)}m over` };
  }
  const remainingMin = Math.round(remainingMs / 60000);
  const totalMs = item.sla_minutes * 60000;
  const warn = remainingMs <= totalMs * 0.2;
  return { state: warn ? 'warn' : 'ok', label: `◷ ${remainingMin}m left` };
}

const PAGE_SIZES = [10, 25, 50, 100] as const;

const STATE_COLOR: Record<CountdownState, string> = {
  ok: 'var(--text-muted)',
  warn: 'var(--orange)',
  breach: 'var(--red)'
};

export function PendingApprovalsPage(): JSX.Element {
  const userQ = useGetCurrentUserQuery();
  const { data } = useGetPendingApprovalsForMeQuery(undefined, { pollingInterval: 30_000 });
  const [now, setNow] = useState(() => Date.now());
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const all = data?.approvals ?? [];
  const pageCount = Math.max(1, Math.ceil(all.length / pageSize));

  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  if (!userQ.data) {
    return <div className="content" style={{ paddingTop: 48 }}>Loading…</div>;
  }
  const pageStart = page * pageSize;
  const items = all.slice(pageStart, pageStart + pageSize);

  return (
    <>
      <h1 className="page-title">Pending approvals</h1>
      <p className="page-sub">
        Approvals for projects you&apos;re a member of. Each shows time remaining against its stage
        SLA; once past target it&apos;s marked breached — the run stays paused until decided.
      </p>

      {all.length === 0 ? (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="empty" style={{ padding: '64px 24px' }}>
            <p className="empty-title">Nothing awaiting your decision</p>
            <p className="empty-sub">Paused approvals for your projects appear here.</p>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((item) => {
            const cd = countdownFor(item, now);
            const breached = cd.state === 'breach';
            return (
              <div
                key={item.approval_id}
                className="card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '14px 18px',
                  border: breached ? '1px solid var(--red)' : undefined
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    {item.doubt_status === 'verdict_pending' ? (
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        verdict awaiting decision
                      </span>
                    ) : item.doubt_status === 'analysing' ? (
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        feedback being investigated
                      </span>
                    ) : breached ? (
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        SLA breached
                      </span>
                    ) : (
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        paused for approval
                      </span>
                    )}
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{item.project_name}</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 8px', fontSize: 11.5 }}>
                    <Meta label="run" value={item.pipeline_run_id} mono />
                    <Meta label="stage" value={item.stage_role} />
                    <Meta label="paused" value={`${formatAge(item.paused_at)} ago`} />
                    {item.sla_minutes !== undefined && <Meta label="target" value={`${item.sla_minutes} min`} />}
                    <Meta label="requested by" value={`${item.requested_by_agent_role || item.stage_role} agent`} />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: STATE_COLOR[cd.state] }}>{cd.label}</span>
                  {item.project_slug && (
                    <Link
                      className="btn sm"
                      to={`/p/${item.project_slug}/runs/${encodeURIComponent(item.pipeline_run_id)}`}
                      data-testid="approval-view-run"
                    >
                      View run
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {all.length > 0 ? (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="pagination">
            <span className="pager-info">
              {`${pageStart + 1}–${pageStart + items.length} of ${all.length}`}
            </span>
            <div className="pager-controls" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span className="mono-cap">Per page</span>
              <select
                className="select sm-sel"
                style={{ width: 80 }}
                aria-label="Approvals per page"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <button
                className="btn ghost sm"
                disabled={page === 0}
                onClick={() => setPage((n) => Math.max(0, n - 1))}
              >
                ← Prev
              </button>
              <span className="pager-info">
                Page {page + 1} of {pageCount}
              </span>
              <button
                className="btn ghost sm"
                disabled={page >= pageCount - 1}
                onClick={() => setPage((n) => Math.min(pageCount - 1, n + 1))}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
